// Tech Support Co-Pilot — John Kirk's portal page (chat + voice).
//
// Left: the deferred email tool ("coming soon"). Right: a chat with the Kirk
// agent, reached through the profile-LOCKED `/api/tech-support-copilot` door (the
// backend forces the `tech_support_portal` profile — the non-technical re-skin of
// the `kirk` agent, dual-KB: product + Quality Signals, read+write).
//
// VOICE (mirrors the Quality Co-Pilot): the agent leads each reply with a short
// spoken `voice_reply` rundown that auto-plays as it streams (synthesized via the
// shared copilot TTS endpoint), and John can talk back via push-to-talk (the
// shared AudioRecorder → Whisper transcribe). Text input stays — John may use
// either. Reuses the shared voice stack end-to-end (useTextToSpeech, AudioRecorder,
// transcribeAudio, makeRundownPlayer) + the Agent Console SSE wire contract.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { AgentFileCard } from '../components/agentConsole/AgentFileCard';
import { AssistantTurn } from '../components/agentConsole/AssistantTurnView';
import { MessageTimestamp } from '../components/agentConsole/MessageTimestamp';
import ModelSelect from '../components/agentConsole/ModelSelect';
import { AttachmentTray, DropVeil } from '../components/chat/AttachmentTray';
import { MessageAttachmentChips } from '../components/chat/MessageAttachmentChips';
import { effectiveMimeType } from '../components/chat/composerAttachments';
import type { MessageAttachment } from '../components/chat/messageAttachments';
import { useComposerAttachments } from '../components/chat/useComposerAttachments';
import {
  turnSetupErrorMessage,
  uploadConversationAttachment,
} from '../services/agentConsole';
import { AudioRecorder } from '../copilot/components/AudioRecorder';
import type { AudioRecordingResult } from '../hooks/useAudioRecording';
import { useTextToSpeech } from '../copilot/hooks/useTextToSpeech';
import { transcribeAudio } from '../copilot/services/copilotApi';
import { GCP_VOICES, DEFAULT_VOICE, formatSpeed } from '../copilot/voiceConfig';
import { makeRundownPlayer } from './qualityCopilotPlayback';
import {
  APPLY_TO_DRAFT_PROMPT,
  detailToTechSupportTurns,
  offerApplyToDraft,
  reduceTurn,
  type AssistantTurnModel,
  type DraftProposal,
  type Turn,
} from './techSupportCopilotTurns';
import {
  cancelMessage,
  conversationForThread,
  createConversation,
  generalConversation,
  getConversationDetail,
  getModels,
  getSignature,
  listConversations,
  renameConversation,
  shouldWarnHistoryFalloff,
  streamTechSupportMessage,
  type ConversationRead,
  type ModelOption,
} from '../services/techSupportCopilot';
import LinkifiedText from '../components/common/LinkifiedText';
import TechSupportMailPanel from '../components/techSupport/TechSupportMailPanel';
import TechSupportSignatureModal from '../components/techSupport/TechSupportSignatureModal';
import { portalService, type PortalConfig } from '../services/portal';
import { resolveSendBanner } from './techSupportBanner';
import { useAuth } from '../context/AuthContext';
import { useGuruWidgetViewContext } from '../hooks/useGuruWidgetViewContext';
import { ConversationHistoryDrawer } from '../components/agentConsole/ConversationHistoryDrawer';
import styles from './TechSupportCopilotPage.module.css';
import { useRecorderObject } from '../hooks/useRecorderObject';

// The Tech Support door's API prefix (matches services/techSupportCopilot BASE).
const TSC_BASE = '/tech-support-copilot';

export default function TechSupportCopilotPage() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // True between a Stop click and the server's terminal cancelled/done frames
  // (the Stop button shows "Stopping…" and won't double-fire).
  const [cancelling, setCancelling] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Voice controls (mirror the Quality Co-Pilot): a Google Cloud TTS voice (it
  // runs on the app's service account, so it works on staging too), a speed
  // slider (1.3× ≈ 30% faster by default), and an auto-play toggle for the spoken
  // rundown. A Voice/Text composer toggle — John may talk or type.
  const [inputMode, setInputMode] = useState<'voice' | 'text'>('voice');
  const [autoplay, setAutoplay] = useState(true);
  const [voiceName, setVoiceName] = useState<string>(DEFAULT_VOICE);
  const [speed, setSpeed] = useState(1.3);
  // The turn whose spoken rundown is currently playing — drives the live
  // "speaking" animation on that turn's rundown bar. Cleared when playback ends.
  const [speakingId, setSpeakingId] = useState<string | null>(null);
  // Selectable models + this door's (Kirk) default — the picker preselects the
  // default; the chosen model rides on the conversation (last-turn-wins).
  const [models, setModels] = useState<ModelOption[]>([]);
  const [defaultModel, setDefaultModel] = useState<string>('');
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  // The strongest selectable model — drives the "try a stronger model" 👎 nudge.
  const [strongestModel, setStrongestModel] = useState<string>('');
  const convIdRef = useRef<string | null>(null);
  // The id of the in-flight turn (from its ``started`` frame) so Stop targets
  // exactly this run — a stale click can never abort a newer turn.
  const runIdRef = useRef<string | null>(null);
  // Files staged for the next Merlin message: drag-drop / paste / picker (the
  // shared hook). Mirrored in a ref so the async send() closure reads the
  // latest staged list. This is the CHAT box's tray — the mail composer's own
  // outbound-email attachments are a separate flow.
  const attach = useComposerAttachments({ disabled: sending });
  const stagedRef = useRef<File[]>([]);
  stagedRef.current = attach.files;
  // The Gmail thread John is currently viewing in the panel (if any), so each
  // turn can tell the agent which email he's asking about. The ref drives the
  // async send() closure; the state drives the scope switcher's render.
  const selectedThreadRef = useRef<{ id: string; subject: string } | null>(null);
  // Bumped when an agent turn finishes so the mail panel re-pulls and shows the
  // agent's freshly proposed draft without John hitting refresh.
  const [mailReloadSignal, setMailReloadSignal] = useState(0);
  // John's signature (auto-appended on send). Loaded once — also seeds the
  // pre-populated default server-side — edited via the modal, and shown in the
  // draft-card preview so what John sees is what goes out.
  const [signatureHtml, setSignatureHtml] = useState('');
  const [sigModalOpen, setSigModalOpen] = useState(false);
  useEffect(() => {
    getSignature()
      .then((s) => setSignatureHtml(s.signature_html))
      .catch(() => undefined);
  }, []);
  // Server runtime config — drives the send-status banner so the page's promise
  // ("does not send" / "redirected to the test address" / live) matches what the
  // Send button actually does in this environment.
  const [portalConfig, setPortalConfig] = useState<PortalConfig | null>(null);
  useEffect(() => {
    portalService
      .getPortalConfig()
      .then(setPortalConfig)
      .catch(() => undefined);
  }, []);
  const sendBanner = resolveSendBanner(portalConfig);
  const [selectedThread, setSelectedThread] = useState<{
    id: string;
    subject: string;
  } | null>(null);
  // The recorder's leave/return markers name the thread open at that moment.
  useRecorderObject({ thread_id: selectedThread?.id });
  // Two chat scopes: the persistent GENERAL conversation (John chatting outside
  // any email), and the per-email conversation bound to the selected thread.
  // Selecting an email switches to its conversation; the switcher flips back to
  // General. Only the EMAIL scope injects the thread context into a turn.
  const [chatScope, setChatScope] = useState<'general' | 'email'>('general');
  const chatScopeRef = useRef<'general' | 'email'>('general');
  chatScopeRef.current = chatScope;
  // Session cache of the General conversation id, resolved from the backend
  // (find-or-create on email_thread_id IS NULL). Only ever holds an UN-bound
  // conversation, so flipping to General can never surface an email thread.
  const generalConvIdRef = useRef<string | null>(null);
  // Snapshot of the transcript for the async send() closure (which can't read
  // fresh state) — lets it tell a conversation's FIRST turn from later ones, so
  // the email-thread context is injected once on the first pass, not every turn.
  const turnsRef = useRef<Turn[]>(turns);
  turnsRef.current = turns;
  const idCounter = useRef(0);
  const logRef = useRef<HTMLDivElement>(null);
  // The locked profile's replayed-history cap, learned from the create response.
  const [historyCap, setHistoryCap] = useState<number | null>(null);
  // Mirror the auto-play toggle into a ref so the async send() closure (which
  // can't read fresh state) honors the current value.
  const autoplayRef = useRef(true);
  autoplayRef.current = autoplay;

  // Conversation history (shared drawer): the caller's own Tech Support threads,
  // remembered per user so a refresh resumes the last one.
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const ownerName = user?.name ?? null;
  const ownerInitials =
    (user?.name || user?.email || '').trim().slice(0, 2).toUpperCase() || null;
  const [conversations, setConversations] = useState<ConversationRead[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Tell the Guru feedback widget which AI conversation + scope is on screen
  // (the mail panel registers its own folder/thread context separately).
  useGuruWidgetViewContext('tech_support_chat', {
    copilot: 'tech-support-copilot',
    chat_scope: chatScope,
    conversation_id: activeId ?? convIdRef.current ?? undefined,
    thread_id: selectedThread?.id,
    thread_subject: selectedThread?.subject,
    model_id: selectedModelId || undefined,
  });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const restoredRef = useRef(false);

  // The Tech Support Co-Pilot speaks via Google Cloud TTS (Chirp 3 HD by
  // default). `speed` live-rates playback; `onComplete` clears the speaking mark.
  const tts = useTextToSpeech({
    voice: voiceName,
    rate: speed,
    provider: 'google',
    onComplete: () => setSpeakingId(null),
  });

  const nextId = () => `t${idCounter.current++}`;

  // Follow the stream: pin to the bottom whenever the transcript grows.
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  // Load the selectable models once (non-fatal): the picker just doesn't render.
  useEffect(() => {
    let cancelled = false;
    getModels()
      .then((data) => {
        if (cancelled) return;
        setModels(data.models);
        setDefaultModel(data.default_model);
        setStrongestModel(data.strongest_model);
        setSelectedModelId((prev) => prev || data.default_model);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Unlock audio autoplay on John's FIRST interaction with the page, so a
  // (delayed) spoken rundown auto-plays instead of being blocked by the browser
  // autoplay policy.
  const primeAudio = tts.prime;
  useEffect(() => {
    const unlock = () => {
      primeAudio();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [primeAudio]);

  const patchAssistant = useCallback(
    (id: string, fn: (t: AssistantTurnModel) => AssistantTurnModel) => {
      setTurns((prev) =>
        prev.map((t) => (t.id === id && t.role === 'assistant' ? fn(t) : t)),
      );
    },
    [],
  );

  // Load the caller's own Tech Support threads for the history drawer (non-fatal).
  const refreshHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      setConversations(await listConversations({ limit: 50 }));
    } catch {
      /* non-fatal — the drawer just shows what it already has */
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const openHistory = useCallback(() => {
    setDrawerOpen(true);
    void refreshHistory();
  }, [refreshHistory]);

  // Resume a saved thread: rebuild its transcript (shared detailToTechSupportTurns,
  // same voice rule as the live stream) and make it the active conversation.
  const loadConversation = useCallback(
    async (id: string) => {
      if (sending) return;
      tts.stop();
      setSpeakingId(null);
      setDrawerOpen(false);
      setError(null);
      convIdRef.current = id;
      setActiveId(id);
      try {
        const detail = await getConversationDetail(id);
        setTurns(detailToTechSupportTurns(detail));
        if (detail.conversation.history_max_turns != null) {
          setHistoryCap(detail.conversation.history_max_turns);
        }
      } catch {
        setError('Could not load that conversation — please try again.');
      }
    },
    [sending, tts],
  );

  // On mount: a ?conversation= deep link wins; otherwise resume the user's
  // GENERAL conversation, resolved from the backend (find-or-create on
  // email_thread_id IS NULL) so it's a distinct, un-bound thread — never a stale
  // email conversation. Runs once, only after we know who the user is.
  useEffect(() => {
    if (restoredRef.current || !userId) return;
    restoredRef.current = true;
    const deepLink = new URLSearchParams(window.location.search).get('conversation');
    if (deepLink) {
      void loadConversation(deepLink);
      return;
    }
    generalConversation(selectedModelId || undefined)
      .then((convo) => {
        generalConvIdRef.current = convo.id;
        void loadConversation(convo.id);
      })
      .catch(() => {
        /* non-fatal — John can start typing and a General conversation is made */
      });
  }, [userId, loadConversation, selectedModelId]);

  // Selecting an email in the panel binds/resumes the ONE co-pilot conversation
  // for that Gmail thread (find-or-create server-side) and switches the chat to
  // it; the next turn also tells the agent which email is selected.
  const handleThreadSelected = useCallback(
    async (threadId: string, subject: string) => {
      selectedThreadRef.current = { id: threadId, subject };
      setSelectedThread({ id: threadId, subject });
      setChatScope('email');
      if (sending) return;
      try {
        const convo = await conversationForThread(
          threadId,
          subject,
          selectedModelId || undefined,
        );
        await loadConversation(convo.id);
      } catch {
        // Binding is best-effort: if it fails, keep the current chat — the
        // first-turn context line still rides along so the agent has the thread.
      }
    },
    [sending, selectedModelId, loadConversation],
  );

  // Flip the chat to the persistent General conversation (the email stays open
  // in the panel on the left — only the chat on the right changes). Resolves the
  // distinct, un-bound General thread from the backend (cached per session after
  // the first resolve) and loads its OWN transcript — never the email's.
  const switchToGeneral = useCallback(async () => {
    if (sending || chatScope === 'general') return;
    setChatScope('general');
    const cached = generalConvIdRef.current;
    if (cached) {
      void loadConversation(cached);
      return;
    }
    try {
      const convo = await generalConversation(selectedModelId || undefined);
      generalConvIdRef.current = convo.id;
      await loadConversation(convo.id);
    } catch {
      // Fall back to a blank General chat; the next message creates one.
      tts.stop();
      setSpeakingId(null);
      convIdRef.current = null;
      setActiveId(null);
      setTurns([]);
      setError(null);
    }
  }, [sending, chatScope, loadConversation, selectedModelId, tts]);

  // Flip the chat back to the currently-open email's bound conversation.
  const switchToEmail = useCallback(() => {
    const open = selectedThreadRef.current;
    if (sending || chatScope === 'email' || !open) return;
    void handleThreadSelected(open.id, open.subject);
  }, [sending, chatScope, handleThreadSelected]);

  // Returning to the inbox (panel "← Inbox") drops the per-turn thread context
  // and flips the chat back to the General conversation.
  const handleThreadCleared = useCallback(() => {
    selectedThreadRef.current = null;
    setSelectedThread(null);
    switchToGeneral();
  }, [switchToGeneral]);

  // Rename a thread (optimistic): update the list immediately, then persist via
  // the shared rename endpoint; on failure, re-fetch to resync the titles.
  const handleRename = useCallback(
    async (id: string, title: string) => {
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, title } : c)),
      );
      try {
        await renameConversation(id, title);
      } catch {
        void refreshHistory();
      }
    },
    [refreshHistory],
  );

  const send = useCallback(
    async (raw: string, viaVoice: boolean) => {
      // A screenshot pasted with no words still sends — the backend hands the
      // file to Merlin (materialized for its Read tool) either way.
      const prompt =
        raw.trim() ||
        (stagedRef.current.length > 0 ? '(see attached file(s))' : '');
      if (!prompt || sending) return;
      setError(null);
      setSending(true);

      // Snapshot + clear the files staged for this turn (they upload below).
      const staged = stagedRef.current;
      if (staged.length > 0) attach.clear();
      const localChips: MessageAttachment[] = staged.map((f, i) => ({
        id: `local-${i}-${f.name}`,
        filename: f.name,
        mimeType: effectiveMimeType(f),
        sizeBytes: f.size,
      }));

      const asstId = nextId();
      const sentAt = new Date().toISOString();
      setTurns((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'user',
          text: prompt,
          viaVoice,
          createdAt: sentAt,
          attachments: localChips.length ? localChips : undefined,
        },
        {
          id: asstId,
          role: 'assistant',
          spoken: null,
          blocks: [],
          draft: null,
          streaming: true,
          createdAt: sentAt,
        },
      ]);

      try {
        let cid = convIdRef.current;
        if (!cid) {
          const convo = await createConversation({
            model: selectedModelId || undefined,
          });
          cid = convo.id;
          convIdRef.current = cid;
          setActiveId(cid);
          // A conversation created from the General scope IS the session's
          // General thread; an email-scope one is re-derived from the panel.
          if (chatScopeRef.current === 'general') {
            generalConvIdRef.current = cid;
          }
          if (convo.history_max_turns != null) setHistoryCap(convo.history_max_turns);
        }
        // Upload the staged files BEFORE the turn so the backend can hand them
        // to Merlin. One failed upload doesn't block the turn.
        for (const file of staged) {
          try {
            await uploadConversationAttachment(TSC_BASE, cid, file);
          } catch {
            /* non-fatal — the turn still runs */
          }
        }
        // Play the spoken rundown the MOMENT its voice_reply frame streams in —
        // once per turn, respecting the auto-play toggle — so it speaks while the
        // agent is still writing (shared makeRundownPlayer; same as the QCP page).
        const playRundown = makeRundownPlayer({
          autoplay: () => autoplayRef.current,
          onPlay: (spoken) => {
            tts.stop();
            tts.speak(spoken);
            setSpeakingId(asstId);
          },
          onStop: () => {
            tts.stop();
            setSpeakingId(null);
          },
        });
        // John's prompt is sent verbatim. The email this chat is bound to is no
        // longer prepended here: the backend weaves the bound Gmail thread id into
        // the kirk SYSTEM prompt (resolved from the conversation's email_thread_id),
        // so the persisted message stays clean and the agent still knows the thread.
        await streamTechSupportMessage(
          cid,
          prompt,
          (evt) => {
            playRundown(evt);
            // Remember this turn's id so a Stop click targets exactly this run.
            if (evt.type === 'started') runIdRef.current = evt.run_id;
            if (evt.type === 'cancelled') {
              // A user Stop — a calm terminal state, never an error banner. The
              // partial reply stays; also silence a rundown already playing.
              tts.stop();
              setSpeakingId(null);
              patchAssistant(asstId, (t) => ({
                ...t,
                cancelled: true,
                streaming: false,
              }));
              return;
            }
            if (evt.type === 'error') {
              setError(evt.message || 'Something went wrong — please try again.');
              patchAssistant(asstId, (t) => ({ ...t, streaming: false }));
              return;
            }
            if (evt.type === 'done') {
              // Capture the real audit message id + the model that answered so the
              // settled turn can carry 👍/👎 feedback (the portal turn is ephemeral).
              patchAssistant(asstId, (t) => ({
                ...t,
                streaming: false,
                messageId: evt.message_id ?? t.messageId ?? null,
                model: evt.model ?? t.model ?? null,
              }));
              return;
            }
            if (evt.type === 'state') {
              patchAssistant(asstId, (t) => ({ ...t, streaming: false }));
              return;
            }
            // Fold every other frame into the turn: blocks (thinking / tool /
            // answer) and the spoken rundown (kept out of the trace).
            patchAssistant(asstId, (t) => ({ ...t, ...reduceTurn(t, evt) }));
          },
          undefined,
          selectedModelId || undefined,
        );
      } catch (e) {
        setError(turnSetupErrorMessage(e, 'Could not reach the assistant.'));
      } finally {
        patchAssistant(asstId, (t) => ({ ...t, streaming: false }));
        setSending(false);
        setCancelling(false);
        runIdRef.current = null;
        // A finished turn may have produced or revised a Gmail draft — nudge the
        // mail panel to re-pull so it shows up without a manual refresh.
        setMailReloadSignal((n) => n + 1);
      }
    },
    [sending, patchAssistant, selectedModelId, tts, attach],
  );

  // Hard-stop the in-flight turn. The server tears the turn down, keeps the
  // partial reply in the thread, and emits the terminal cancelled/done frames on
  // the existing stream — those settle the UI (send()'s finally re-enables the
  // composer), so this handler only fires the request.
  const handleStop = useCallback(async () => {
    const convId = convIdRef.current;
    if (!convId || cancelling) return;
    setCancelling(true);
    try {
      await cancelMessage(convId, runIdRef.current);
    } catch {
      /* the turn may have just finished — the stream's frames settle the UI */
    }
  }, [cancelling]);

  // Voice input: transcribe the recording (shared Whisper endpoint), then send it
  // as a normal turn. "Transcribing…" clears the moment transcription resolves —
  // send() drives its own "sending" state for the reply.
  const handleAudio = useCallback(
    async (result: AudioRecordingResult) => {
      setTranscribing(true);
      setError(null);
      let text = '';
      try {
        const { transcript } = await transcribeAudio(result.blob, result.mimeType);
        text = transcript.trim();
      } catch {
        setError('Could not transcribe that — please try again or type it.');
        return;
      } finally {
        setTranscribing(false);
      }
      if (!text) {
        setError("I didn't catch that — please try again.");
        return;
      }
      await send(text, true);
    },
    [send],
  );

  const replay = useCallback(
    (turnId: string, spoken: string) => {
      setSpeakingId(turnId);
      tts.stop();
      tts.speak(spoken);
    },
    [tts],
  );

  const stopSpeaking = useCallback(() => {
    tts.stop();
    setSpeakingId(null);
  }, [tts]);

  const submitText = useCallback(() => {
    const v = input;
    setInput('');
    void send(v, false);
  }, [input, send]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitText();
    }
  };

  const startNew = () => {
    if (sending) return;
    tts.stop();
    setSpeakingId(null);
    convIdRef.current = null;
    generalConvIdRef.current = null;
    setActiveId(null);
    // "New chat" is a fresh GENERAL conversation (the panel selection is left
    // as-is; the switcher still offers the open email). Clearing the cached id
    // means the next send creates a new un-bound conversation.
    setChatScope('general');
    setTurns([]);
    setError(null);
    setDrawerOpen(false);
  };

  // Once John has sent `historyCap` messages, the agent's replayed-history window
  // starts dropping the earliest turns — warn so he can reset or adapt.
  const userTurns = turns.filter((t) => t.role === 'user').length;
  const warnFalloff = shouldWarnHistoryFalloff(userTurns, historyCap);

  return (
    <div className={styles.page}>
      <div className={styles.stage}>
        {/* LEFT: John's live Tech Support inbox (reads the bound mailbox via the
            co-pilot mail proxy; the agent's proposed drafts show up here too). */}
        <div className={styles.mail}>
          <TechSupportMailPanel
            onThreadSelected={handleThreadSelected}
            onThreadCleared={handleThreadCleared}
            reloadSignal={mailReloadSignal}
            signatureHtml={signatureHtml}
            onEditSignature={() => setSigModalOpen(true)}
          />
        </div>

        <TechSupportSignatureModal
          isOpen={sigModalOpen}
          initialHtml={signatureHtml}
          onClose={() => setSigModalOpen(false)}
          onSaved={setSignatureHtml}
        />

        {/* RIGHT: the Kirk agent chat */}
        <section className={styles.chat} aria-label="Merlin chat">
          {/* Send-status notice, resolved from the server's runtime config
              (drafts-only / sandbox-redirect / suppressed / live). It lives at the
              top of the Merlin column — where drafting and Send happen — rather than
              a full-width bar above the whole page. That earlier full-width bar sat
              BETWEEN the portal header and the inbox and pushed the inbox down out
              of view (John read the shorter list as "emails are missing"); keeping
              it in this column lets the inbox span the full height under the navbar. */}
          {sendBanner && (
            <div className={styles.testingBanner} role="status">
              🧪 {sendBanner.text}
            </div>
          )}
          <header className={styles.chatHead}>
            <span className={styles.dot} aria-hidden="true" />
            <div className={styles.headTitle}>
              <span className={styles.chatTitle}>Merlin</span>
              <span className={styles.chatMeta}>
                Tech Support · speaks &amp; listens · product &amp; quality knowledge · learns from you
              </span>
            </div>
            <div className={styles.controls}>
              <button
                type="button"
                className={`${styles.ctrlPill} ${autoplay ? '' : styles.ctrlPillOff}`}
                onClick={() => {
                  // Turning auto-play OFF should also silence whatever is playing.
                  if (autoplay) tts.stop();
                  setAutoplay((a) => !a);
                }}
                title="Auto-play spoken replies"
                aria-pressed={autoplay}
                data-action="tsc_autoplay_toggle"
              >
                <span className={styles.ctrlDot} />
                {autoplay ? 'Voice on' : 'Voice off'}
              </button>
              <label className={styles.speed} title="Voice speed">
                <span aria-hidden="true">⏩</span>
                <input
                  type="range"
                  min={0.7}
                  max={2}
                  step={0.05}
                  value={speed}
                  onChange={(e) => setSpeed(parseFloat(e.target.value))}
                  aria-label="Voice speed"
                />
                <span className={styles.speedVal}>{formatSpeed(speed)}</span>
              </label>
              <select
                className={styles.voiceSelect}
                value={voiceName}
                onChange={(e) => setVoiceName(e.target.value)}
                title="Spoken voice"
                aria-label="Spoken voice"
              >
                {GCP_VOICES.map((v) => (
                  <option key={v.name} value={v.name}>
                    {v.label}
                  </option>
                ))}
              </select>
              {models.length > 0 && (
                <ModelSelect
                  models={models}
                  value={selectedModelId || defaultModel}
                  onChange={setSelectedModelId}
                  disabled={sending}
                />
              )}
              <button
                type="button"
                className={styles.newBtn}
                onClick={() => setSigModalOpen(true)}
                title="Edit your email signature (auto-appended to replies)"
                data-action="tsc_signature_edit"
                data-outcome="open"
                data-record-value="source"
                value="page_header"
              >
                ✎ Signature
              </button>
              <button
                type="button"
                className={styles.newBtn}
                onClick={openHistory}
                title="Your past conversations"
                data-action="tsc_history_open"
                data-outcome="open"
              >
                📚 History
              </button>
              <button
                type="button"
                className={styles.newBtn}
                onClick={startNew}
                disabled={sending}
                data-action="tsc_chat_new"
              >
                New chat
              </button>
            </div>
          </header>

          {/* Scope switcher — only when an email is open. Flip the chat between
              John's General conversation and the selected email's own thread. */}
          {selectedThread && (
            <div
              className={styles.scopeBar}
              role="tablist"
              aria-label="Conversation scope"
            >
              <button
                type="button"
                role="tab"
                aria-selected={chatScope === 'general'}
                className={`${styles.scopeTab} ${
                  chatScope === 'general' ? styles.scopeTabOn : ''
                }`}
                onClick={switchToGeneral}
                disabled={sending}
                data-action="tsc_chat_scope_tab"
              >
                💬 General
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={chatScope === 'email'}
                className={`${styles.scopeTab} ${
                  chatScope === 'email' ? styles.scopeTabOn : ''
                }`}
                onClick={switchToEmail}
                disabled={sending}
                title={selectedThread.subject}
                aria-label="Email thread"
                data-action="tsc_chat_scope_tab"
                data-target-thread-id={selectedThread.id}
              >
                <span className={styles.scopeTabIcon} aria-hidden="true">
                  📧
                </span>
                <span className={styles.scopeTabLabel}>{selectedThread.subject}</span>
              </button>
            </div>
          )}

          <div className={styles.log} ref={logRef}>
            {turns.length === 0 && (
              <div className={styles.intro}>
                <div className={styles.introTitle}>Hi John 👋</div>
                Tap the mic and just talk, or type. Ask me about ChemMasters
                products, specs &amp; DOT/QPL status, competitor cross-references,
                lot/QC data, or quality issues. I&apos;ll answer out loud and on
                screen — correct me when I&apos;m wrong and I&apos;ll remember it.
              </div>
            )}
            {turns.map((t, i) => {
              // A pipeline-initiated turn (the auto-draft kickoff) is not John's
              // words — show a faint system note, not a 'you' bubble.
              if (t.role === 'system') {
                return (
                  <div key={t.id} className={styles.sysNote} role="note">
                    <span aria-hidden="true">⚙︎ </span>
                    {t.note}
                    {t.createdAt ? (
                      <> · <MessageTimestamp at={t.createdAt} /></>
                    ) : null}
                  </div>
                );
              }
              return t.role === 'user' ? (
                <div key={t.id} className={styles.userWrap}>
                  <div className={styles.userMsg}>
                    {t.text}
                    <MessageAttachmentChips attachments={t.attachments} />
                  </div>
                  <div className={styles.userCap}>
                    {t.viaVoice ? '🎙️ transcribed from voice' : '⌨️ typed'}
                    {t.createdAt ? (
                      <> · <MessageTimestamp at={t.createdAt} /></>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div key={t.id} className={styles.asstWrap}>
                  {/* Spoken rundown — leads the reply; plays as it streams in.
                      PREPARING (TTS round-trip) → spinner; PLAYING → equalizer;
                      IDLE → "tap to play". */}
                  {t.spoken && (
                    <VoiceRundownBar
                      spoken={t.spoken}
                      speaking={tts.isSpeaking && speakingId === t.id}
                      synthesizing={tts.isSynthesizing && speakingId === t.id}
                      onReplay={() => replay(t.id, t.spoken as string)}
                      onStop={stopSpeaking}
                    />
                  )}
                  {(t.blocks.length > 0 || t.streaming) && (
                    <div className={styles.asstMsg}>
                      <AssistantTurn
                        blocks={t.blocks}
                        isStreaming={t.streaming}
                        feedback={
                          !t.streaming && t.messageId && activeId
                            ? {
                                basePath: '/tech-support-copilot',
                                conversationId: activeId,
                                messageId: t.messageId,
                                modelUsed: t.model ?? null,
                                strongestModelId: strongestModel || null,
                                strongestModelLabel:
                                  models.find((m) => m.id === strongestModel)?.label ??
                                  null,
                              }
                            : null
                        }
                      />
                      {t.streaming && t.blocks.length === 0 && !t.spoken && (
                        <span className={styles.thinking}>Looking things up…</span>
                      )}
                    </div>
                  )}
                  {/* The reply the agent just DRAFTED into Gmail — shown here the
                      moment it writes it. Read-only; John sends it from the inbox
                      panel on the left (the agent never sends). */}
                  {t.draft && <DraftProposalCard draft={t.draft} />}
                  {/* Files the agent attached this turn (attach_file) — one
                      downloadable card each (images preview inline). */}
                  {t.agentFiles?.map((file) => (
                    <AgentFileCard
                      key={file.attachmentId}
                      base={TSC_BASE}
                      file={file}
                      conversationId={activeId}
                    />
                  ))}
                  {/* A user Stop is a calm terminal state, never an error: the
                      partial reply above stays, and the composer is ready. */}
                  {t.cancelled && !t.streaming && (
                    <div className={styles.stoppedNote}>
                      <span aria-hidden="true">⏹</span> Stopped — reply
                      interrupted. You can keep chatting in this thread.
                    </div>
                  )}
                  {/* Recovery bridge: Merlin wrote/revised a reply in chat but
                      did NOT save it to the Gmail draft — one click asks it to
                      apply this response to the editable draft (the agent
                      writes the clean body; chat text is narration). */}
                  {offerApplyToDraft({
                    turn: t,
                    isLast: i === turns.length - 1,
                    emailScope: chatScope === 'email',
                    sending,
                  }) && (
                    <div className={styles.applyRow}>
                      <button
                        type="button"
                        className={styles.applyBtn}
                        onClick={() => void send(APPLY_TO_DRAFT_PROMPT, false)}
                        data-action="tsc_apply_to_draft"
                        data-outcome="request"
                      >
                        ✉️ Put this in the email draft
                      </button>
                      <span className={styles.applyHint}>
                        saves this response as a draft you can edit &amp; send
                        from the inbox
                      </span>
                    </div>
                  )}
                  {/* When Merlin answered — persisted created_at on resume. */}
                  {!t.streaming && t.createdAt ? (
                    <div className={styles.turnStampRow}>
                      <MessageTimestamp at={t.createdAt} />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          {error && (
            <div className={styles.errorBar} role="alert">
              {error}
            </div>
          )}

          {warnFalloff && (
            <div className={styles.warnBar} role="status">
              Heads up — this chat is getting long, so I only keep the last{' '}
              {historyCap} exchanges in mind; earlier details may start dropping
              off. Start a <strong>New chat</strong> to reset, or re-state anything
              important. (Facts worth keeping are saved to the knowledge bank — and
              you can ask to raise the limit.)
            </div>
          )}

          <div className={styles.composer} {...attach.dropZoneProps}>
            <DropVeil active={attach.isDragging} />
            <AttachmentTray
              files={attach.files}
              attachError={attach.attachError}
              onAddFiles={attach.addFiles}
              onRemoveFile={attach.removeFile}
              disabled={sending}
            />
            <div className={styles.modeSwitch} role="tablist" aria-label="Input mode">
              {(
                [
                  { mode: 'voice', label: '🎤 Voice' },
                  { mode: 'text', label: '⌨️ Text' },
                ] as const
              ).map(({ mode, label }) => (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={inputMode === mode}
                  className={`${styles.modeBtn} ${
                    inputMode === mode ? styles.modeBtnOn : ''
                  }`}
                  onClick={() => setInputMode(mode)}
                  data-action="tsc_input_mode_tab"
                >
                  {label}
                </button>
              ))}
            </div>

            {inputMode === 'voice' ? (
              <div className={styles.voiceMode}>
                {transcribing ? (
                  <div className={styles.transcribing}>
                    <span className={styles.spinner} /> Transcribing…
                  </div>
                ) : sending ? (
                  // While Merlin works, the mic yields to Stop (standard chat
                  // pattern): halt the turn, keep the partial reply, same thread.
                  <button
                    type="button"
                    className={styles.stopTurnBtn}
                    onClick={() => void handleStop()}
                    disabled={cancelling}
                    title="Stop this response"
                    aria-label="Stop this response"
                    data-action="tsc_chat_stop"
                    data-outcome="request"
                  >
                    <span className={styles.stopSquare} aria-hidden="true" />
                    {cancelling ? 'Stopping…' : 'Stop'}
                  </button>
                ) : (
                  <AudioRecorder
                    onRecordingComplete={handleAudio}
                    recordLabel="Tap to talk"
                    maxDuration={120}
                    compact
                    fallback={
                      <p className={styles.fallbackNote}>
                        Voice input isn&apos;t available in this browser — switch to
                        Text.
                      </p>
                    }
                  />
                )}
              </div>
            ) : (
              <div className={styles.inputRow}>
                <textarea
                  className={styles.textarea}
                  placeholder="Message Merlin… (drag, paste, or attach files)"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  onPaste={attach.onPaste}
                  rows={1}
                />
                {sending ? (
                  // While Merlin works, Send becomes Stop (standard chat pattern).
                  <button
                    type="button"
                    className={styles.stopTurnBtn}
                    onClick={() => void handleStop()}
                    disabled={cancelling}
                    title="Stop this response"
                    aria-label="Stop this response"
                    data-action="tsc_chat_stop"
                    data-outcome="request"
                  >
                    <span className={styles.stopSquare} aria-hidden="true" />
                    {cancelling ? 'Stopping…' : 'Stop'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.sendBtn}
                    onClick={submitText}
                    disabled={!input.trim() && attach.files.length === 0}
                    data-action="tsc_chat_send"
                    data-outcome="request"
                  >
                    Send
                  </button>
                )}
              </div>
            )}
            <div className={styles.foot}>
              Spoken summary is read aloud · full detail stays on screen ·
              conversations &amp; corrections are saved.
            </div>
          </div>
        </section>

        {/* The history drawer overlays the whole stage (absolute within .stage),
            so its closed state slides fully off the left edge. */}
        <ConversationHistoryDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          conversations={conversations}
          activeId={activeId}
          loading={loadingHistory}
          ownerName={ownerName}
          ownerInitials={ownerInitials}
          onSelect={(id) => void loadConversation(id)}
          onNew={startNew}
          onRename={(id, title) => void handleRename(id, title)}
        />
      </div>
    </div>
  );
}

// The reply the agent proposed this turn (its create_draft / update_draft tool),
// mirrored from the inbox panel into the chat so John sees the exact email the
// moment it's written — subject + full body. Read-only: the human Send lives in
// the inbox panel (left), where the draft also appears; the agent never sends.
function DraftProposalCard({ draft }: { draft: DraftProposal }) {
  return (
    <div className={styles.draftCard}>
      <div className={styles.draftHead}>
        <span className={styles.draftLabel}>✎ Proposed reply</span>
        <span className={styles.draftHint}>saved to your drafts — send from the inbox →</span>
      </div>
      {draft.subject && <div className={styles.draftSubject}>{draft.subject}</div>}
      {draft.to && <div className={styles.draftTo}>To: {draft.to}</div>}
      {/* URLs render as real links (new tab) so John can test them here. */}
      <div className={styles.draftBody}>
        <LinkifiedText text={draft.body} />
      </div>
      {(draft.attachCount ?? 0) > 0 && (
        <div className={styles.draftTo}>
          📎 attaches {draft.attachCount} product document
          {draft.attachCount === 1 ? '' : 's'} — the files are on the draft in the
          inbox panel
        </div>
      )}
    </div>
  );
}

// One assistant turn's spoken-rundown bar: a play/stop control + the spoken text,
// with three live states driven by the TTS hook — PREPARING (synthesis round-trip
// in flight) → spinner, PLAYING → equalizer, IDLE → "tap to play". The flip from
// spinner to equalizer is the cue that synthesis just finished and audio plays.
function VoiceRundownBar({
  spoken,
  speaking,
  synthesizing,
  onReplay,
  onStop,
}: {
  spoken: string;
  speaking: boolean;
  synthesizing: boolean;
  onReplay: () => void;
  onStop: () => void;
}) {
  return (
    <div
      className={`${styles.voice} ${speaking ? styles.voiceSpeaking : ''} ${
        synthesizing ? styles.voiceSynth : ''
      }`}
    >
      <button
        type="button"
        className={styles.voicePlay}
        onClick={() => (speaking ? onStop() : onReplay())}
        data-action="tsc_rundown_toggle"
        aria-label={
          synthesizing
            ? 'Preparing spoken rundown'
            : speaking
              ? 'Stop spoken rundown'
              : 'Play spoken rundown'
        }
      >
        {synthesizing ? (
          <span className={styles.voiceSpinner} aria-hidden="true" />
        ) : speaking ? (
          '❚❚'
        ) : (
          '▶'
        )}
      </button>
      <div className={styles.voiceMid}>
        <div className={styles.voiceTop}>
          <span className={styles.voiceLabel}>Spoken rundown</span>
          {synthesizing ? (
            <span className={styles.voiceState}>Preparing voice…</span>
          ) : speaking ? (
            <span className={styles.eq} aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
              <i />
            </span>
          ) : (
            <span className={styles.voiceState}>tap to play</span>
          )}
        </div>
        <p className={styles.voiceCap}>{spoken}</p>
      </div>
    </div>
  );
}
